import { computed, type Ref } from 'vue';
import { facilityFromRouteOrLastVisited, yearFromRouteOrLastVisited } from '@/router';
import { slugFromName } from '../../../tri-shared/services/formatting';
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import { navActiveStatesUpdate, type NavMenuGroup, type NavMenuSection } from '../../../cross-app-frontend/navigation';
import { facilityPermissions } from '@/services/navigation/navigation-permissions';

export function useTriNavMenu(currentRoute: Ref<RouteLocationNormalizedLoadedGeneric>): Ref<NavMenuSection[]> {
  return computed((): NavMenuSection[] => {
    const facility = facilityFromRouteOrLastVisited.value;
    if (!facility) {
      return [];
    }

    const facilityKey = facility.facilityID as unknown as number;
    const perms = facilityPermissions.value[facilityKey];
    const hasUpdateBaseFacts = perms?.hasUpdateBaseFacts ?? false;
    const hasUpdateFacility = perms?.hasUpdateFacility ?? false;
    const hasAdministerFacility = perms?.hasAdministerFacility ?? false;
    const hasManageUsers = perms?.hasManageUsers ?? false;
    const isSuperAdmin = perms?.isSuperAdmin ?? false;

    const isAdmin = hasAdministerFacility;
    const isSupervisor = !isAdmin && hasUpdateFacility;
    const isDataPreparer = !isAdmin && !isSupervisor && hasUpdateBaseFacts;

    const facilityParams = {
      facilityID: facility.facilityID,
      facilitySlug: slugFromName(facility.name),
      year: yearFromRouteOrLastVisited.value,
    };

    let workflowGroups: NavMenuGroup[] = [
      {
        title: 'Overview',
        children: [
          {
            title: 'Facility Status',
            icon: 'fa-building',
            type: 'link',
            isActive: false,
            to: {
              name: 'Facility Status',
              params: facilityParams,
            },
          },
        ],
      },
      {
        title: 'Base Facts',
        children: [
          {
            title: 'Products',
            icon: 'fa-boxes-stacked',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Products', params: facilityParams },
          },
          {
            title: 'Activity',
            icon: 'fa-temperature-half',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Usage', params: facilityParams },
          },
          {
            title: 'Activity Review',
            icon: 'fa-file-chart-column',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Activity Review', params: facilityParams },
          },
        ],
      },
      {
        title: 'Thresholds',
        children: [
          {
            title: 'Exemptions',
            icon: 'fa-clipboard-check',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Exemption Review', params: facilityParams },
          },
          {
            title: 'Threshold Review',
            icon: 'fa-table',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Thresholds', params: facilityParams },
          },
        ],
      },
      {
        title: 'Releases',
        children: [
          {
            title: 'Assign Release Profiles',
            icon: 'fa-screwdriver-wrench',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Release Profiles', params: facilityParams },
          },
          {
            title: 'Questionnaire',
            icon: 'fa-clipboard-question',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Questionnaire', params: facilityParams },
          },
          {
            title: 'Release Review',
            icon: 'fa-table-tree',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Release Review', params: facilityParams },
          },
        ],
      },
      {
        title: 'Submission',
        children: [
          {
            title: 'Basic Info',
            icon: 'fa-id-card',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Form R Details', params: facilityParams },
          },
          {
            title: 'Final Review',
            icon: 'fa-file-alt',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Form R', params: facilityParams },
          },
          {
            title: 'Submit',
            icon: 'fa-file-export',
            type: 'link',
            isActive: false,
            to: { name: 'Facility Form R Submit', params: facilityParams },
          },
        ],
      },
    ];

    const helpGroups: NavMenuGroup[] = [
      {
        title: 'Help & Support',
        children: [
          {
            title: 'TRI Knowledge',
            icon: 'fa-book-open',
            type: 'link',
            isActive: false,
            to: { name: 'TRI Knowledge', params: facilityParams },
          },
          // TODO: 9. Help - Bring this back once we've got some content:
          // {
          //   title: 'App Help',
          //   icon: 'fa-question-circle',
          //   type: 'link',
          //   isActive: false,
          //   to: { name: 'App Help', params: facilityParams },
          // },
        ],
      },
    ];

    const adminGroups: NavMenuGroup[] = [
      {
        title: 'Admin',
        children: [
          {
            title: 'Overall Status',
            icon: 'fa-city',
            type: 'collapsible',
            isActive: false,
            pages: [
              {
                title: 'Facility Status',
                icon: 'fa-chart-line',
                to: {
                  name: 'Overall Status',
                  params: facilityParams,
                },
                isActive: false,
              },
              {
                title: 'Anomalies',
                icon: 'fa-triangle-exclamation',
                to: {
                  name: 'Anomalies',
                  params: facilityParams,
                },
                isActive: false,
              },
              {
                title: 'Mass Actions',
                icon: 'fa-users-gear',
                to: {
                  name: 'Mass Actions',
                  params: facilityParams,
                },
                isActive: false,
              },
              {
                title: 'Assignment Changes',
                icon: 'fa-code-compare',
                to: {
                  name: 'Admin Release Profile Assignments Comparison',
                  params: facilityParams,
                },
                isActive: false,
              },
              {
                title: 'Release Comparison',
                icon: 'fa-chart-line-up',
                to: {
                  name: 'Admin Release Comparison',
                  params: facilityParams,
                },
                isActive: false,
              },
              {
                title: 'Export Comparison',
                icon: 'fa-camera-retro',
                to: {
                  name: 'Admin Release Snapshot Comparison',
                  params: facilityParams,
                },
                isActive: false,
              },
            ],
          },
          {
            title: 'Release Profiles',
            icon: 'fa-clouds',
            type: 'link',
            isActive: false,
            to: { name: 'Release Profiles', params: facilityParams },
          },
          {
            title: 'Chemicals',
            icon: 'fa-flask-vial',
            type: 'link',
            isActive: false,
            to: { name: 'Chemicals', params: facilityParams },
          },
          {
            title: 'Receptors',
            icon: 'fa-truck-fast',
            type: 'link',
            isActive: false,
            to: { name: 'Receptors', params: facilityParams },
          },
          {
            title: 'e-Manifest',
            icon: 'fa-truck-container',
            type: 'link',
            isActive: false,
            to: { name: 'E-Manifest', params: facilityParams },
          },
          ...(isAdmin
            ? [
                {
                  title: 'Usage Import',
                  icon: 'fa-file-import',
                  type: 'link' as const,
                  isActive: false,
                  to: { name: 'Usage Import' as const, params: facilityParams },
                },
              ]
            : []),
          ...(isSuperAdmin
            ? [
                {
                  title: 'Ivy',
                  icon: 'fa-leaf',
                  type: 'link' as const,
                  isActive: false,
                  to: { name: 'Spreadsheet Agent' as const, params: facilityParams },
                },
              ]
            : []),
          {
            title: 'Facilities',
            icon: 'fa-city',
            type: 'link',
            isActive: false,
            to: { name: 'Facilities', params: facilityParams },
          },
          {
            title: 'Users',
            icon: 'fa-users',
            type: 'link',
            isActive: false,
            to: { name: 'Users', params: facilityParams },
          },
        ],
      },
    ];

    if (isDataPreparer) {
      // Data Preparers: Facility Status, Products, Activity, Activity Review, Questionnaire only.
      workflowGroups = workflowGroups
        .map((group) => {
          if (group.title === 'Overview') {
            return group;
          }
          if (group.title === 'Base Facts') {
            return group;
          }
          if (group.title === 'Releases') {
            return {
              ...group,
              children: group.children.filter(
                (child) =>
                  child.type === 'link' &&
                  child.to &&
                  typeof child.to === 'object' &&
                  'name' in child.to &&
                  child.to.name === 'Facility Questionnaire',
              ),
            };
          }

          return { ...group, children: [] };
        })
        .filter((group) => group.children.length > 0);
    } else if (isSupervisor && !isAdmin) {
      // Supervisors: all TRI Workflow pages except Submit.
      workflowGroups = workflowGroups.map((group) => {
        if (group.title === 'Submission') {
          return {
            ...group,
            children: group.children.filter(
              (child) =>
                child.type !== 'link' ||
                !child.to ||
                typeof child.to !== 'object' ||
                !('name' in child.to) ||
                child.to.name !== 'Facility Form R Submit',
            ),
          };
        }
        return group;
      });
    }

    const result: NavMenuSection[] = [
      {
        id: 'workflow',
        title: 'TRI Workflow',
        groups: workflowGroups,
      },
      ...(isAdmin || hasManageUsers
        ? [
            {
              id: 'admin',
              title: 'Admin',
              groups: adminGroups,
            } as NavMenuSection,
          ]
        : []),
      {
        id: 'help',
        title: 'Help & Support',
        groups: helpGroups,
      },
    ];

    navActiveStatesUpdate(currentRoute.value, result);

    return result;
  });
}
